import { Dropdown, DropdownProps, MenuProps } from "antd";
import React, { CSSProperties, ReactNode, useMemo } from "react";
import DropdownMenuLabel from "./DropdownMenuLabel";
import { Colors } from "../utils/colors";

export type DropdownMenuItem = {
  title: string;
  subtext?: ReactNode;
  icon?: string;
  iconColor?: string;
  key: string;
  onPress: () => void;
  isDestructive?: boolean;
  labelColor?: string;
  disabled?: boolean;
  loading?: boolean;
};

type DropdownMenuProps = {
  dropdownItems: DropdownMenuItem[];
  trigger?: DropdownProps["trigger"];
  placement?: DropdownProps["placement"];
  children: React.ReactNode;
  isOpen?: boolean; // Used if we want to manage state on the dropdown child (ie. up and down arrow)
  onOpenChange?: (
    open: boolean,
    info?: {
      source: "trigger" | "menu";
    }
  ) => void;
  altWidth?: number;
  topPadding?: number; // gap between component and the dropdown menu overlay
  overlayStyle?: CSSProperties;
  disabled?: boolean;
  dark?: boolean;
};

export const DropdownMenu = (props: DropdownMenuProps) => {
  const {
    dropdownItems,
    trigger,
    placement,
    children,
    onOpenChange,
    isOpen,
    disabled,
    altWidth,
    topPadding,
    overlayStyle,
    dark,
  } = props;

  const items: MenuProps["items"] = useMemo(
    () =>
      dropdownItems.map((item) => {
        return {
          label: (
            <DropdownMenuLabel
              label={item.title}
              subtext={item.subtext}
              icon={item.icon}
              iconColor={item.iconColor}
              width={altWidth}
              isDestructive={item.isDestructive}
              labelColor={item.labelColor}
              loading={item.loading}
              disabled={item.disabled}
              dark={dark}
            />
          ),
          key: item.key,
          disabled: item.disabled || item.loading,
        };
      }),
    [dropdownItems, altWidth, dark]
  );

  const onClick: MenuProps["onClick"] = ({ key }) => {
    const foundItem = dropdownItems.find((item) => item.key === key);
    if (foundItem) {
      foundItem.onPress();
    }
  };

  return (
    <Dropdown
      menu={{
        items,
        onClick,
        style: {
          backgroundColor: dark ? Colors.GRAY10 : Colors.WHITE,
          border: `0.5px solid ${dark ? Colors.GRAY3 : Colors.GRAY2}`,
        },
      }}
      trigger={trigger}
      disabled={disabled}
      placement={placement}
      overlayStyle={{ paddingTop: topPadding ?? 7, ...overlayStyle }}
      onOpenChange={(isOpen: boolean, info) =>
        onOpenChange ? onOpenChange(isOpen, info) : undefined
      }
      open={isOpen}
    >
      <div style={{ cursor: disabled ? "auto" : "pointer" }}>{children}</div>
    </Dropdown>
  );
};
